import { yupResolver } from "@hookform/resolvers/yup";
import { Box, IconButton, Stack, Typography, useTheme } from "@mui/material";
import {
  Button,
  CloseIcon,
  EditIcon,
  Input,
  SelectField,
  TruncatedText,
} from "components";
import { useGetAllCustomerSites } from "controller";
import { useAtomValue, useSetAtom } from "jotai";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import type {
  AddCustomerUserFormValues,
  CustomerSiteResultType,
  PendingCustomerUser,
} from "shared";
import { addUserSchema } from "./BasicInformationForm";
import {
  CategorySelectField,
  useCustomerUserCategoryOptions,
} from "./CategorySelectField";
import { CrownTierIcon } from "shared/features/customer-user-category";
import { PERMISSION_OPTIONS, PERMISSION_TOOLTIP } from "./constants";
import { DUPLICATE_EMAIL_MESSAGE, isDuplicatePendingEmail } from "./helpers";
import {
  pendingCustomerUsersAtom,
  removePendingCustomerUserAtom,
  updatePendingCustomerUserAtom,
} from "../../store/addCustomerUsersAtoms";

const InfoChip = ({
  label,
  value,
  icon,
}: {
  label: string;
  value: string;
  icon?: React.ReactNode;
}) => {
  const theme = useTheme();
  return (
    <Stack
      direction="row"
      alignItems="center"
      gap="4px"
      sx={{
        height: "24px",
        padding: "4px 8px",
        borderRadius: "4px",
        border: `1px solid ${theme.palette.neutral["015"]}`,
        backgroundColor: "#F6F6F5",
        maxWidth: "200px",
        overflow: "hidden",
        whiteSpace: "nowrap",
      }}
    >
      {icon}
      <Typography
        component="span"
        variant="body-xsmall"
        sx={{ color: theme.palette.lightBg.low, flexShrink: 0 }}
      >
        {`${label}: `}
      </Typography>
      <Typography
        component="span"
        variant="body-xsmall"
        sx={{
          fontWeight: 600,
          color: theme.palette.lightBg.high,
          overflow: "hidden",
          textOverflow: "ellipsis",
          minWidth: 0,
        }}
      >
        {value}
      </Typography>
    </Stack>
  );
};

const EditPendingUser = ({
  user,
  customerId,
  onDone,
}: {
  user: PendingCustomerUser;
  customerId: string;
  onDone: () => void;
}) => {
  const theme = useTheme();
  const updateUser = useSetAtom(updatePendingCustomerUserAtom);
  const pendingUsers = useAtomValue(pendingCustomerUsersAtom);

  const { data: sitesData } = useGetAllCustomerSites({
    customerId,
    sortColumn: "site_name",
    sortDirection: "asc",
  });
  const siteOptions = useMemo(
    () =>
      Array.isArray(sitesData?.result)
        ? sitesData.result.filter(
            (site: CustomerSiteResultType) => site?.is_active,
          )
        : [],
    [sitesData?.result],
  );
  const { options: categoryOptions } = useCustomerUserCategoryOptions();

  const {
    control,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<AddCustomerUserFormValues>({
    defaultValues: {
      name: user.name,
      email: user.email,
      site: user.siteValue,
      permission: user.isPrivileged ? "privileged" : "standard",
      category: user.categoryValue,
    },
    resolver: yupResolver(addUserSchema),
  });

  const onSave = handleSubmit((values) => {
    const email = values.email.trim();

    // Same rule as the add form (shared helper so the two cannot drift). Two
    // rows sharing an address make `applyCustomerUserBatchResultAtom`'s
    // email-keyed reconciliation ambiguous: a partial result would drop BOTH
    // rows, so a rejection would vanish with no reason shown and no row to retry.
    if (
      isDuplicatePendingEmail(
        email,
        pendingUsers.filter((row) => row.id !== user.id),
      )
    ) {
      setError("email", {
        type: "duplicate",
        message: DUPLICATE_EMAIL_MESSAGE,
      });
      return;
    }

    const site = siteOptions.find(
      (option: CustomerSiteResultType) => option.mapping_id === values.site,
    );
    const category = categoryOptions.find(
      (option) => option.mapping_id === values.category,
    );

    updateUser({
      ...user,
      name: values.name.trim(),
      email,
      siteValue: values.site ?? "",
      siteLabel: site?.site_name ?? null,
      isPrivileged: values.permission === "privileged",
      categoryValue: values.category ?? "",
      categoryLabel: category?.name ?? null,
      categoryTier: category?.tier ?? null,
    });
    onDone();
  });

  return (
    <Stack
      sx={{
        padding: "16px 16px 8px 16px",
        borderRadius: "8px",
        border: `1px solid ${theme.palette.neutral["015"]}`,
        backgroundColor: theme.palette.neutral.main,
        boxShadow: "0px 0px 4px 0px #0000000F, 0px 0px 2px 0px #0000000A",
        gap: "8px",
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          rowGap: "16px",
          columnGap: "16px",
        }}
      >
        <Input
          control={control}
          name="name"
          label="Name"
          required
          placeholder="Enter name"
          error={!!errors.name}
          errorMessage={errors.name?.message}
        />
        <Input
          control={control}
          name="email"
          label="Email"
          required
          placeholder="Enter email"
          error={!!errors.email}
          errorMessage={errors.email?.message}
        />
        <SelectField
          control={control}
          name="site"
          label="Site"
          placeholder="Select site"
          options={siteOptions}
          valueMappingKey="mapping_id"
          labelMappingKey="site_name"
          isSearchable
        />
        <SelectField
          control={control}
          name="permission"
          label="Permission"
          tooltip={PERMISSION_TOOLTIP}
          placeholder="Select permission"
          options={PERMISSION_OPTIONS}
          valueMappingKey="value"
          labelMappingKey="label"
        />
        <Box sx={{ gridColumn: "1/-1" }}>
          <CategorySelectField control={control} />
        </Box>
      </Box>
      <Stack direction="row" justifyContent="flex-end">
        <Button
          variant="ghost"
          onClick={onSave}
          sx={{
            padding: "2px 8px",
            minWidth: 0,
            height: "24px",
            fontSize: "12px",
            lineHeight: "16px",
          }}
        >
          Save changes
        </Button>
      </Stack>
    </Stack>
  );
};

export const PendingUserCard = ({
  user,
  customerId,
}: {
  user: PendingCustomerUser;
  customerId: string;
}) => {
  const theme = useTheme();
  const [isEditing, setIsEditing] = useState(false);
  const removeUser = useSetAtom(removePendingCustomerUserAtom);

  if (isEditing) {
    return (
      <EditPendingUser
        user={user}
        customerId={customerId}
        onDone={() => setIsEditing(false)}
      />
    );
  }

  const hasFailed = Boolean(user.failedReason);

  return (
    <Stack
      sx={{
        position: "relative",
        gap: "8px",
        padding: "16px",
        borderRadius: "8px",
        backgroundColor: theme.palette.neutral.main,
        border: hasFailed
          ? `1px solid ${theme.palette.error.main}`
          : `1px solid ${theme.palette.neutral["015"]}`,
        // CSS hover, not JS state — a mouse crossing the queue would otherwise
        // re-render a card per pointer move.
        "& .pending-user-actions": { opacity: 0, pointerEvents: "none" },
        "&:hover .pending-user-actions": { opacity: 1, pointerEvents: "auto" },
      }}
    >
      <Stack
        className="pending-user-actions"
        direction="row"
        sx={{
          position: "absolute",
          top: "12px",
          right: "12px",
          gap: "4px",
          transition: "opacity 150ms ease",
        }}
      >
        <IconButton
          size="small"
          aria-label={`Edit ${user.name}`}
          onClick={() => setIsEditing(true)}
        >
          <EditIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          aria-label={`Remove ${user.name}`}
          onClick={() => removeUser(user.id)}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>

      {/* minWidth 0 so the long unbroken values can shrink instead of forcing
          the card wider than the queue. The name keeps clear of the hover
          actions pinned to the top-right corner. */}
      <Stack gap="2px" sx={{ minWidth: 0 }}>
        <TruncatedText
          text={user.name}
          maxWidth="calc(100% - 56px)"
          sx={{
            ...theme.typography["body-medium"],
            color: theme.palette.lightBg.high,
          }}
        />
        <TruncatedText
          text={user.email}
          maxWidth="100%"
          sx={{
            ...theme.typography["body-small"],
            color: theme.palette.lightBg.low,
          }}
        />
      </Stack>

      <Stack direction="row" gap="8px" flexWrap="wrap">
        {user.siteLabel && <InfoChip label="Site" value={user.siteLabel} />}
        <InfoChip
          label="Permission"
          value={user.isPrivileged ? "Privileged" : "Standard"}
        />
        {user.categoryLabel && (
          <InfoChip
            label="Category"
            value={user.categoryLabel}
            icon={<CrownTierIcon tier={user.categoryTier} size={14} />}
          />
        )}
      </Stack>

      {hasFailed && (
        <Typography
          variant="body-xsmall"
          sx={{ color: theme.palette.error.main }}
        >
          {user.failedReason}
        </Typography>
      )}
    </Stack>
  );
};
